/**
 * All locale-branched SEO copy for the tax-iceberg page in one place: title, meta description,
 * H1 (base page and salary slug pages), the glossary blurbs, the tax-card section and the FAQ.
 * The page, +title.ts and +Head.tsx all read from here, so the visible FAQ and the FAQPage
 * JSON-LD are the same text and the tab title matches the meta tags.
 *
 * Keyword targets (Ahrefs, Finland, 2026-09): fi "veroprosenttilaskuri" (20k/mo),
 * "bruttopalkka" (14k), "verokortti" (8.5k), "verolaskuri" (6.1k), "nettopalkka" (6.1k),
 * "palkkalaskuri" (5.6k), "verokiila" (450); sv "bruttolön", "nettolön", "skatteprocenträknare";
 * en "tax calculator finland", "finland salary calculator".
 *
 * Follows the hand-branched pattern the page's Head already used (not the message catalogue).
 * The year is never typed by hand: it comes from the tax parameters that drive the calculator.
 */

import { TAX_PARAMS_2026 } from '../../utils/salary/calculate-finnish-net-salary';
import { parseTaxIcebergSlug } from '../../utils/salary/tax-iceberg-url';

export type CopyLocale = 'en' | 'fi' | 'sv';

export function asCopyLocale(locale: string | undefined): CopyLocale {
  return locale === 'fi' || locale === 'sv' ? locale : 'en';
}

export const TAX_YEAR = TAX_PARAMS_2026.year;

function money(locale: CopyLocale, amount: number): string {
  return new Intl.NumberFormat(locale === 'fi' ? 'fi-FI' : locale === 'sv' ? 'sv-SE' : 'en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
}
function pct(locale: CopyLocale, value: number, digits = 1): string {
  return new Intl.NumberFormat(locale === 'fi' ? 'fi-FI' : locale === 'sv' ? 'sv-SE' : 'en-IE', { style: 'percent', maximumFractionDigits: digits }).format(value / 100);
}

/** Base page: title (with the tax year on fi only), meta description, H1 (no year: it is the
 * most visible element and a stale year there reads as a correctness failure). */
export function pageCopy(locale: CopyLocale): { title: string; description: string; h1: string } {
  if (locale === 'fi') {
    return {
      title: `Palkkalaskuri ja veroprosenttilaskuri ${TAX_YEAR}`,
      description: 'Laske nettopalkka bruttopalkasta ja veroprosenttisi ilmaiseksi. Näet myös työnantajan kulut ja piilotetut verot: kattava palkkalaskuri.',
      h1: 'Palkkalaskuri ja veroprosenttilaskuri',
    };
  }
  if (locale === 'sv') {
    return {
      title: 'Löneräknare Finland: nettolön och bruttolön',
      description: 'Räkna ut din nettolön från bruttolön och se din skatteprocent. Gratis löneräknare för Finland, med arbetsgivarens kostnader och dolda skatter.',
      h1: 'Löneräknare Finland: nettolön och bruttolön',
    };
  }
  return {
    title: 'Finland Tax Calculator: Net Salary from Gross',
    description: 'Calculate your net salary from gross pay and see your real tax rate. Free Finland salary calculator that also shows employer costs and hidden taxes.',
    h1: 'Finland Salary & Tax Calculator',
  };
}

/** A salary slug page (`/tax-iceberg/4000-gross-income`): its own title, description and H1. */
export function salaryPageCopy(locale: CopyLocale, slug: string | undefined): { title: string; description: string; h1: string } | null {
  const salary = parseTaxIcebergSlug(slug);
  if (!salary) return null;
  const amount = money(locale, salary.gross);
  const year = salary.period === 'year';
  if (salary.mode === 'net') return netSalaryPageCopy(locale, amount, year);
  // Titles stay under about 60 characters with the brand suffix; the H1 can be fuller.
  if (locale === 'fi') {
    const per = year ? 'vuodessa' : 'kuukaudessa';
    const short = year ? '/v' : '/kk';
    return {
      title: `${amount}${short} bruttopalkka: nettopalkka ja veroprosentti`,
      description: `Palkkalaskuri ${amount} bruttopalkalle ${per}: nettopalkka, veroprosentti ja työnantajan sivukulut. Vuoden ${TAX_YEAR} verotiedoilla, jokainen luku lähteistetty.`,
      h1: `${amount} bruttopalkka ${per}: nettopalkka ja veroprosentti`,
    };
  }
  if (locale === 'sv') {
    const per = year ? 'per år' : 'per månad';
    const short = year ? '/år' : '/mån';
    return {
      title: `${amount}${short} bruttolön: nettolön och skatteprocent`,
      description: `Löneräknare för ${amount} bruttolön ${per}: nettolön, skatteprocent, arbetsgivarens kostnader och vad du får behålla. Varje siffra är källhänvisad.`,
      h1: `${amount} bruttolön ${per}: nettolön och skatteprocent`,
    };
  }
  const per = year ? 'a year' : 'a month';
  const short = year ? '/year' : '/month';
  return {
    title: `${amount}${short} gross in Finland: net salary and tax`,
    description: `Finland salary calculator for a ${amount} gross salary ${per}: net salary, tax rate, employer costs and what you actually keep. Every figure is sourced.`,
    h1: `${amount} gross salary ${per} in Finland: net salary and tax`,
  };
}

/** A target-net page (`/veroprosenttilaskuri/3000-nettopalkka`): the question is which gross it takes. */
function netSalaryPageCopy(locale: CopyLocale, amount: string, year: boolean): { title: string; description: string; h1: string } {
  if (locale === 'fi') {
    const per = year ? 'vuodessa' : 'kuukaudessa';
    const short = year ? '/v' : '/kk';
    return {
      title: `${amount}${short} nettopalkka: paljonko bruttopalkkaa tarvitset?`,
      description: `Laske, kuinka suuri bruttopalkka jättää ${amount} käteen ${per}. Veroprosentti, työnantajan sivukulut ja koko verojäävuori vuoden ${TAX_YEAR} verotiedoilla.`,
      h1: `${amount} nettopalkka ${per}: tarvittava bruttopalkka ja veroprosentti`,
    };
  }
  if (locale === 'sv') {
    const per = year ? 'per år' : 'per månad';
    const short = year ? '/år' : '/mån';
    return {
      title: `${amount}${short} nettolön: vilken bruttolön behöver du?`,
      description: `Räkna ut vilken bruttolön som ger ${amount} i handen ${per}: skatteprocent, arbetsgivarens kostnader och hela skatteisberget.`,
      h1: `${amount} nettolön ${per}: bruttolönen som krävs och skatteprocenten`,
    };
  }
  const per = year ? 'a year' : 'a month';
  const short = year ? '/year' : '/month';
  return {
    title: `${amount}${short} net in Finland: what gross do you need?`,
    description: `Find the gross salary that leaves ${amount} in hand ${per} in Finland: tax rate, employer cost and the whole tax iceberg.`,
    h1: `${amount} net salary ${per} in Finland: the gross you need`,
  };
}

/** Numbers the FAQ answers quote, computed from the calculator for the salary on the page. */
export interface CopyFacts {
  /** Gross in the period shown (monthly unless the slug says per year). */
  gross: number;
  net: number;
  trueEmployerCost: number;
  perYear: boolean;
  /** Taxes only (what a tax card's withholding rate covers), as % of gross. */
  taxRatePct: number;
  /** Taxes plus the employee's pension and unemployment contributions, as % of gross. */
  totalRatePct: number;
}

export function glossary(locale: CopyLocale): { gross: string; net: string } {
  if (locale === 'fi') {
    return {
      gross: 'Bruttopalkka on palkkasi ennen veroja ja maksuja: se summa, joka lukee työsopimuksessa. Luontoisedut lasketaan siihen mukaan.',
      net: 'Nettopalkka on tilille maksettava summa: bruttopalkka miinus ennakonpidätys sekä työntekijän eläke- ja työttömyysvakuutusmaksut.',
    };
  }
  if (locale === 'sv') {
    return {
      gross: 'Bruttolön är din lön före skatt och avgifter: beloppet i arbetsavtalet, inklusive naturaförmåner.',
      net: 'Nettolön är det som betalas till kontot: bruttolön minus förskottsinnehållning samt pensions- och arbetslöshetsförsäkringsavgifter.',
    };
  }
  return {
    gross: 'Gross salary is your pay before tax and contributions: the figure in your employment contract, fringe benefits included.',
    net: 'Net salary is what lands in your account: gross pay minus withholding tax and your pension and unemployment insurance contributions.',
  };
}

export function taxCardSection(locale: CopyLocale): { heading: string; paragraphs: string[] } | null {
  if (locale !== 'fi') return null;
  return {
    heading: 'Verokortti ja veroprosentti: miten ne liittyvät laskuriin',
    paragraphs: [
      'Verokortti on Verohallinnon ilmoitus ennakonpidätysprosentistasi ja tulorajastasi. Työnantaja saa verokortin suoraan Verohallinnolta ja pidättää palkastasi veroa sen mukaan. Prosentti on laskettu arviosta, paljonko ansaitset koko vuoden aikana.',
      `Tämän laskurin veroprosentti vastaa verokortin logiikkaa: se sisältää valtion tuloveron, kunnallisveron, mahdollisen kirkollisveron, sairaanhoitomaksun ja Yle-veron vähennysten jälkeen. Työeläkemaksu (${pct('fi', TAX_PARAMS_2026.pensionRate * 100)}) ja työttömyysvakuutusmaksu (${pct('fi', TAX_PARAMS_2026.unemploymentRate * 100, 2)}) pidätetään palkasta verokortin prosentin päälle, ja ne näkyvät laskurissa erikseen.`,
      'Jos tulosi muuttuvat kesken vuoden, tilaa muutosverokortti OmaVerossa. Liian pieni prosentti johtaa jäännösveroon eli mätkyihin, liian suuri veronpalautukseen.',
    ],
  };
}

export function faqHeading(locale: CopyLocale): string {
  return locale === 'fi' ? 'Usein kysyttyä palkasta ja veroista' : locale === 'sv' ? 'Vanliga frågor om lön och skatt' : 'Frequently asked questions about salary and tax in Finland';
}

export function faqItems(locale: CopyLocale, f: CopyFacts): Array<{ question: string; answer: string }> {
  const m = (v: number) => money(locale, v);
  const p = (v: number) => pct(locale, v);
  if (locale === 'fi') {
    const per = f.perYear ? 'vuodessa' : 'kuukaudessa';
    return [
      {
        question: 'Mikä on bruttopalkka?',
        answer: 'Bruttopalkka on palkkasi ennen veroja ja maksuja: se summa, joka lukee työsopimuksessa ja palkkalaskelman ylärivillä. Siitä vähennetään verokortin mukainen ennakonpidätys sekä työntekijän eläke- ja työttömyysvakuutusmaksut, ja jäljelle jää nettopalkka. Luontoisedut, kuten puhelin- tai autoetu, lasketaan bruttopalkkaan.',
      },
      {
        question: 'Mitä nettopalkka tarkoittaa?',
        answer: `Nettopalkka on tilille maksettava summa: bruttopalkka, josta on vähennetty ennakonpidätys, työeläkemaksu (${pct('fi', TAX_PARAMS_2026.pensionRate * 100)}) ja työttömyysvakuutusmaksu (${pct('fi', TAX_PARAMS_2026.unemploymentRate * 100, 2)}). Esimerkiksi ${m(f.gross)} bruttopalkasta ${per} jää tällä laskurilla käteen noin ${m(f.net)}.`,
      },
      {
        question: 'Miten veroprosentti lasketaan?',
        answer: `Veroprosentti lasketaan arvioiduista koko vuoden tuloista. Siihen sisältyvät progressiivinen valtion tulovero, kunnallisvero (keskimäärin ${p(TAX_PARAMS_2026.avgMunicipalRatePct)}), mahdollinen kirkollisvero, sairaanhoitomaksu ja Yle-vero, ja siitä on vähennetty muun muassa työtulovähennys. Tällä laskurilla ${m(f.gross)} bruttopalkan ${per} veroprosentti on noin ${p(f.taxRatePct)}; eläke- ja työttömyysvakuutusmaksujen kanssa palkasta lähtee yhteensä noin ${p(f.totalRatePct)}.`,
      },
      {
        question: 'Mikä on verokortti?',
        answer: 'Verokortti on Verohallinnon ilmoitus ennakonpidätysprosentistasi ja tulorajastasi. Työnantaja saa sen suoraan Verohallinnolta ja pidättää veron palkasta sen mukaan. Jos tulosi muuttuvat kesken vuoden, tilaa muutosverokortti OmaVerossa: liian pieni prosentti johtaa mätkyihin, liian suuri veronpalautukseen.',
      },
      {
        question: 'Mikä on verokiila?',
        answer: `Verokiila on ero sen välillä, mitä työnantajalle maksaa työllistää sinut, ja sen, mitä saat käteen. Siihen kuuluvat työnantajan sivukulut, sinun verosi ja maksusi sekä kulutusverot, kun käytät nettopalkkasi. ${m(f.gross)} bruttopalkalla ${per} työnantajan kokonaiskustannus on noin ${m(f.trueEmployerCost)}. Jäävuori näyttää verokiilan: pinnan yläpuolella on se osa, joka jää sinulle.`,
      },
      {
        question: 'Kuinka paljon veroa palkasta menee?',
        answer: `Se riippuu tuloista, kunnasta ja vähennyksistä. ${m(f.gross)} bruttopalkasta ${per} menee tällä laskurilla veroja noin ${p(f.taxRatePct)} ja veroja ja maksuja yhteensä noin ${p(f.totalRatePct)}, eli käteen jää noin ${m(f.net)}. Syötä oma palkkasi laskuriin ja valitse kuntasi, niin näet oman lukusi.`,
      },
    ];
  }
  if (locale === 'sv') {
    return [
      {
        question: 'Vad är bruttolön?',
        answer: `Bruttolön är din lön före skatt och avgifter: beloppet i arbetsavtalet, naturaförmåner inräknade. Från den dras förskottsinnehållningen enligt skattekortet samt pensionsavgiften (${pct('sv', TAX_PARAMS_2026.pensionRate * 100)}) och arbetslöshetsförsäkringsavgiften (${pct('sv', TAX_PARAMS_2026.unemploymentRate * 100, 2)}); resten är nettolönen. Med ${m(f.gross)} bruttolön ${f.perYear ? 'per år' : 'per månad'} blir nettolönen enligt räknaren cirka ${m(f.net)}.`,
      },
    ];
  }
  const per = f.perYear ? 'a year' : 'a month';
  return [
    {
      question: 'How is income tax calculated in Finland?',
      answer: `Finnish income tax on wages is progressive state tax plus a flat municipal tax (on average ${p(TAX_PARAMS_2026.avgMunicipalRatePct)}, set by your municipality), plus a health care contribution, the Yle tax and church tax if you are a member, less credits such as the earned-income credit. On top of tax, your employer withholds pension (${pct('en', TAX_PARAMS_2026.pensionRate * 100)}) and unemployment insurance (${pct('en', TAX_PARAMS_2026.unemploymentRate * 100, 2)}) contributions. For a ${m(f.gross)} gross salary ${per} this calculator puts tax at about ${p(f.taxRatePct)} and tax plus contributions at about ${p(f.totalRatePct)}, leaving roughly ${m(f.net)} net.`,
    },
    {
      question: 'What is a Finnish tax card (verokortti)?',
      answer: 'A tax card (verokortti) is the Finnish Tax Administration\'s notice of your withholding rate and income ceiling. Your employer receives it electronically and withholds tax from each payslip at that rate. The rate is an estimate based on your expected income for the whole year; if your income changes, order a revised card in MyTax (OmaVero), otherwise you end up with back taxes or a refund the following year.',
    },
  ];
}
